/**
 * E05-F09 — REAL adapter for the prompt-run journal + the test replay.
 *
 * Replaces the prototype mock (`../mock/promptRuns.ts`, deleted with this
 * change) now that the endpoints exist:
 *
 *   GET  /admin/prompts/:promptKey/runs      → usePromptRuns()      (E05-F08-T04)
 *   GET  /admin/prompts/:promptKey/runs/:id  → usePromptRun()       (E05-F08-T04)
 *   POST /admin/prompts/:promptKey/test      → useTestPrompt()      (E05-F08-T05)
 *
 * Same shape as the sibling `promptsApi.ts`: the shared `apiClient` (injects the
 * Bearer token, normalizes the envelope, never throws), `unwrap` at the Query
 * boundary, and the response validated against the `@eer/shared` contract so the
 * window can never render a shape the server does not actually send.
 *
 * TWO THINGS THIS MODULE MUST NOT DO (E05-F09-T02 acceptance):
 *   - the test call NEVER writes the template. `POST …/test` only reads
 *     `ai_prompts`; the draft travels in the body and dies with the request.
 *     Saving is the separate `useUpdatePrompt` (PATCH) behind its own button.
 *   - one press = one call. No `retry` on the mutation, and the query layer must
 *     not re-issue it: a retry here would be a second PAID model call (the
 *     2026-07-20 money-leak policy).
 *
 * A FAILED MODEL CALL IS A 200 with `error` set — it is a normal outcome of a
 * test, not an HTTP failure, and it carries the diagnostics (`renderedPrompt`,
 * `missingVars`) the admin pressed the button for.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  adminPromptRunResponseSchema,
  adminPromptRunsListResponseSchema,
  adminPromptTestResponseSchema,
  type PromptRunDetail,
  type PromptRunSummary,
  type PromptTestResult,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../../api/client.js';
import type { PromptTestRequest } from '../promptTestModel.js';

/** The legacy command label the API logs requests under (sibling parity). */
const COMMAND = 'manage_prompts';

// ---------------------------------------------------------------------------
// Query keys (feature-local, kept out of the shared registry)
// ---------------------------------------------------------------------------

export function promptRunsQueryKey(promptKey: string): readonly unknown[] {
  return ['admin', 'prompts', promptKey, 'runs'] as const;
}

export function promptRunQueryKey(promptKey: string, runId: string): readonly unknown[] {
  return ['admin', 'prompts', promptKey, 'runs', runId] as const;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

async function fetchPromptRuns(promptKey: string): Promise<PromptRunSummary[]> {
  const result = await apiClient.get<unknown>(
    `/admin/prompts/${encodeURIComponent(promptKey)}/runs`,
    { command: COMMAND },
  );
  return adminPromptRunsListResponseSchema.parse(unwrap(result)).runs;
}

async function fetchPromptRun(promptKey: string, runId: string): Promise<PromptRunDetail> {
  const result = await apiClient.get<unknown>(
    `/admin/prompts/${encodeURIComponent(promptKey)}/runs/${encodeURIComponent(runId)}`,
    { command: COMMAND },
  );
  return adminPromptRunResponseSchema.parse(unwrap(result)).run;
}

async function postPromptTest(req: PromptTestRequest): Promise<PromptTestResult> {
  const result = await apiClient.post<unknown>(
    `/admin/prompts/${encodeURIComponent(req.promptKey)}/test`,
    // `.strict()` body on the server: runId + the optional draft, nothing else.
    // The prompt key travels in the path, the actor in the session token.
    req.template === undefined
      ? { runId: req.runId }
      : { runId: req.runId, template: req.template },
    { command: COMMAND },
  );
  return adminPromptTestResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * The recorded runs of one prompt, newest first. An EMPTY array is a normal,
 * expected answer — most prompts have never been exercised — and the window
 * says so instead of showing an empty form.
 */
export function usePromptRuns(
  promptKey: string,
): UseQueryResult<PromptRunSummary[], ApiResultError> {
  return useQuery<PromptRunSummary[], ApiResultError>({
    queryKey: promptRunsQueryKey(promptKey),
    queryFn: () => fetchPromptRuns(promptKey),
    // A test run appends a row; the list must not serve a stale journal.
    staleTime: 0,
  });
}

/** One full run: its values, the prompt that was sent, the answer that came back. */
export function usePromptRun(
  promptKey: string,
  runId: string | null,
): UseQueryResult<PromptRunDetail, ApiResultError> {
  return useQuery<PromptRunDetail, ApiResultError>({
    queryKey: promptRunQueryKey(promptKey, runId ?? ''),
    enabled: runId !== null,
    queryFn: () => fetchPromptRun(promptKey, runId ?? ''),
  });
}

/**
 * Run the DRAFT template against one recorded run's values — one paid model
 * call, explicitly `retry: false` (see the module docblock).
 *
 * On success the run list is invalidated: the call wrote an `origin='test'` row
 * that must appear in the selector, marked as a replay.
 */
export function useTestPrompt(): UseMutationResult<
  PromptTestResult,
  ApiResultError,
  PromptTestRequest
> {
  const queryClient = useQueryClient();
  return useMutation<PromptTestResult, ApiResultError, PromptTestRequest>({
    mutationFn: postPromptTest,
    retry: false,
    onSuccess: (_result, req) => {
      void queryClient.invalidateQueries({ queryKey: promptRunsQueryKey(req.promptKey) });
    },
  });
}
